import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../api";
import type {
  CommercialProduct,
  Id,
  PatientIssue,
  PatientLtv,
  PostcareEvent,
  Professional,
  RetentionOpportunity,
  ReviewRequest,
  TreatmentEvent,
} from "../types";
import { useToast } from "../context/ToastContext";
import { useAuth } from "../context/AuthContext";
import { formatCurrency, formatDateTime } from "../lib/format";

const LTV_ROLES = ["admin", "director", "sales_manager", "sales", "marketing"];
const TREATMENT_ROLES = ["admin", "director", "reception", "professional", "sales", "sales_manager"];

const POSTCARE_LABELS: Record<string, string> = {
  CHECKIN: "Acompañamiento",
  FOLLOWUP_24H: "Seguimiento 24h",
  FOLLOWUP_72H: "Seguimiento 72h",
  SATISFACTION: "Encuesta de satisfacción",
  PROFESSIONAL_CONTROL: "Control profesional",
};

function canResolveIssue(role: string | undefined, issue: PatientIssue): boolean {
  if (!role) return false;
  if (issue.severity === "URGENT") return ["admin", "director", "professional"].includes(role);
  return ["admin", "director", "professional", "reception"].includes(role);
}

export default function PostventaTab({ contactId }: { contactId: Id }) {
  const { user } = useAuth();
  const { show } = useToast();

  const [products, setProducts] = useState<CommercialProduct[]>([]);
  const [professionals, setProfessionals] = useState<Professional[]>([]);
  const [ltv, setLtv] = useState<PatientLtv | null>(null);
  const [issues, setIssues] = useState<PatientIssue[]>([]);
  const [opportunities, setOpportunities] = useState<RetentionOpportunity[]>([]);
  const [reviews, setReviews] = useState<ReviewRequest[]>([]);
  const [loading, setLoading] = useState(false);

  // Treatment registration
  const [productId, setProductId] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  const [notes, setNotes] = useState("");
  const [registering, setRegistering] = useState(false);
  const [lastTreatment, setLastTreatment] = useState<TreatmentEvent | null>(null);
  const [postcareEvents, setPostcareEvents] = useState<PostcareEvent[]>([]);

  // Satisfaction / review
  const [csat, setCsat] = useState("");
  const [submittingSurvey, setSubmittingSurvey] = useState(false);
  const [requestingReview, setRequestingReview] = useState(false);
  const [resolvingId, setResolvingId] = useState<Id | null>(null);

  function reload() {
    setLoading(true);
    Promise.all([
      api.getIssues({ contact_id: contactId }).catch(() => []),
      api.getRetentionOpportunities({ contact_id: contactId, upcoming: true }).catch(() => []),
      api.getReviewRequests({ contact_id: contactId }).catch(() => []),
    ])
      .then(([i, o, r]) => {
        setIssues(i);
        setOpportunities(o);
        setReviews(r);
      })
      .finally(() => setLoading(false));

    if (user && LTV_ROLES.includes(user.role_code)) {
      api
        .getPatientLtv(contactId)
        .then(setLtv)
        .catch(() => setLtv(null));
    }
  }

  useEffect(() => {
    api.getCommercialProducts().then(setProducts).catch(() => {});
    api.getProfessionals().then(setProfessionals).catch(() => {});
    reload();
    setLastTreatment(null);
    setPostcareEvents([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactId]);

  const canRegisterTreatment = !!user && TREATMENT_ROLES.includes(user.role_code);

  async function registerTreatment() {
    if (!productId) {
      show("Elegí un tratamiento realizado.", "error");
      return;
    }
    setRegistering(true);
    try {
      const result = await api.registerTreatment({
        contact_id: contactId,
        product_id: productId,
        professional_id: professionalId || undefined,
        notes: notes.trim() || undefined,
      });
      setLastTreatment(result.treatment_event);
      setPostcareEvents(result.postcare_events || []);
      show("Tratamiento registrado. Postventa materializada.", "success");
      setNotes("");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar el tratamiento.", "error");
    } finally {
      setRegistering(false);
    }
  }

  async function submitSurvey() {
    if (!lastTreatment) return;
    const n = Number(csat);
    if (!n || n < 1 || n > 5) {
      show("Ingresá un CSAT entre 1 y 5.", "error");
      return;
    }
    setSubmittingSurvey(true);
    try {
      await api.submitSatisfaction({ treatment_event_id: lastTreatment.id, csat: n });
      show("Encuesta de satisfacción registrada.", "success");
      setCsat("");
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo registrar la satisfacción.", "error");
    } finally {
      setSubmittingSurvey(false);
    }
  }

  async function requestReview() {
    if (!lastTreatment) return;
    setRequestingReview(true);
    try {
      await api.requestReview({ treatment_event_id: lastTreatment.id });
      show("Solicitud de reseña enviada.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo solicitar la reseña.", "error");
    } finally {
      setRequestingReview(false);
    }
  }

  async function resolveIssue(issue: PatientIssue) {
    setResolvingId(issue.id);
    try {
      await api.resolveIssue(issue.id, {});
      show("Incidencia resuelta.", "success");
      reload();
    } catch (e) {
      if (e instanceof ApiClientError) show(e.message, "error");
      else show("No se pudo resolver la incidencia.", "error");
    } finally {
      setResolvingId(null);
    }
  }

  return (
    <div>
      {ltv && (
        <>
          <h3>LTV del paciente</h3>
          <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
            <div className="stat-tile">
              <div className="muted">LTV bruto</div>
              <div className="big">{formatCurrency(ltv.ltv_gross)}</div>
            </div>
            <div className="stat-tile">
              <div className="muted">LTV BCM</div>
              <div className="big">{formatCurrency(ltv.ltv_bcm)}</div>
            </div>
            <div className="stat-tile">
              <div className="muted">Ticket promedio</div>
              <div className="big">{formatCurrency(ltv.avg_ticket)}</div>
            </div>
            <div className="stat-tile">
              <div className="muted">Próxima ventana</div>
              <div className="big" style={{ fontSize: 13 }}>{formatDateTime(ltv.next_expected_purchase_at)}</div>
            </div>
          </div>
        </>
      )}

      <h3 style={{ marginTop: 16 }}>Incidencias abiertas</h3>
      {issues.filter((i) => i.status !== "RESOLVED").length === 0 && <p className="meta">Sin incidencias abiertas.</p>}
      {issues
        .filter((i) => i.status !== "RESOLVED")
        .map((issue) => (
          <div key={issue.id} className="opp-item">
            <div className="name">
              <span className={`pill ${issue.severity === "URGENT" ? "pill-bad" : ""}`}>{issue.severity}</span> {issue.issue_type}
            </div>
            <div className="meta">{issue.description || "Sin descripción."}</div>
            <div className="meta">Abierta {formatDateTime(issue.opened_at)}</div>
            {canResolveIssue(user?.role_code, issue) ? (
              <button className="link-btn" disabled={resolvingId === issue.id} onClick={() => resolveIssue(issue)}>
                {resolvingId === issue.id ? "Resolviendo..." : "Resolver"}
              </button>
            ) : (
              <span className="meta">Requiere profesional/Dirección</span>
            )}
          </div>
        ))}

      <h3 style={{ marginTop: 16 }}>Reseñas / satisfacción</h3>
      {reviews.length === 0 && <p className="meta">Sin solicitudes de reseña todavía.</p>}
      {reviews.map((r) => (
        <div key={r.id} className="opp-item">
          <div className="name">
            <span className={`pill ${r.status === "RECEIVED" ? "pill-ok" : r.status === "DECLINED" ? "pill-bad" : "pill-warn"}`}>{r.status}</span>{" "}
            {r.platform || ""}
          </div>
          {r.rating !== null && r.rating !== undefined && <div className="meta">Rating: {r.rating}/5</div>}
        </div>
      ))}

      <h3 style={{ marginTop: 16 }}>Oportunidades de retención</h3>
      {opportunities.length === 0 && <p className="meta">Sin oportunidades de reventa/recurrencia.</p>}
      {opportunities.map((o) => (
        <div key={o.id} className="opp-item">
          <div className="name">
            {o.opportunity_type} — <span className="pill">{o.status}</span>
          </div>
          <div className="meta">Elegible desde {formatDateTime(o.eligible_at)}</div>
        </div>
      ))}

      {canRegisterTreatment && (
        <>
          <h3 style={{ marginTop: 16 }}>Registrar tratamiento realizado</h3>
          <select value={productId} onChange={(e: any) => setProductId(e.target.value)}>
            <option value="">Elegí el tratamiento...</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          <select value={professionalId} onChange={(e: any) => setProfessionalId(e.target.value)}>
            <option value="">Profesional (opcional)...</option>
            {professionals.map((p) => (
              <option key={p.id} value={p.id}>
                {p.full_name}
              </option>
            ))}
          </select>
          <textarea placeholder="Notas del tratamiento" rows={2} value={notes} onChange={(e: any) => setNotes(e.target.value)} />
          <button className="btn-ghost" disabled={registering} onClick={registerTreatment}>
            {registering ? "Registrando..." : "Registrar tratamiento"}
          </button>

          {lastTreatment && (
            <div style={{ marginTop: 12 }}>
              <h4>Postcare de tratamiento #{lastTreatment.id}</h4>
              <div className="timeline">
                {postcareEvents.map((pe) => (
                  <div className="event" key={pe.id}>
                    <b>{POSTCARE_LABELS[pe.event_type] || pe.event_type}</b>
                    <small>{formatDateTime(pe.scheduled_at)}</small>
                    <div className="meta">
                      <span className={`pill ${pe.status === "COMPLETED" ? "pill-ok" : pe.status === "CANCELLED" ? "pill-bad" : "pill-warn"}`}>
                        {pe.status}
                      </span>
                    </div>
                  </div>
                ))}
                {postcareEvents.length === 0 && <p className="meta">Sin eventos de postventa generados.</p>}
              </div>

              <h4>Encuesta de satisfacción</h4>
              <div className="row" style={{ gap: 8 }}>
                <input placeholder="CSAT 1-5" style={{ width: 90 }} value={csat} onChange={(e: any) => setCsat(e.target.value)} />
                <button className="link-btn" disabled={submittingSurvey} onClick={submitSurvey}>
                  {submittingSurvey ? "Enviando..." : "Registrar CSAT"}
                </button>
              </div>

              <div className="row" style={{ marginTop: 8 }}>
                <button className="link-btn" disabled={requestingReview} onClick={requestReview}>
                  {requestingReview ? "Solicitando..." : "Solicitar reseña"}
                </button>
              </div>
              <p className="meta">
                Sólo se puede solicitar reseña con CSAT ≥ 4, sin incidencias abiertas y control completado si
                corresponde — el servidor vuelve a validar esto en cada solicitud.
              </p>
            </div>
          )}
        </>
      )}

      {!canRegisterTreatment && (
        <p className="meta" style={{ marginTop: 16 }}>
          Tu rol no puede registrar tratamientos desde acá.
        </p>
      )}
    </div>
  );
}
